import css from './DataForm.module.css';

import {
  Alignment,
  DataStatus,
  FormStatus,
  Spacing,
  StatusUtil,
} from '../../../types/appTypes';
import Card from '../Card/Card';
import Container from '../Container/Container';
import StatusText from '../StatusText/StatusText';
import Spinner from '../Spinner/Spinner';
import Visibility from '../Visibility/Visibility';

type FormProps = {
  title?: string;
  successMessage?: string;
  successActionMessage?: string;
  successAction?: React.MouseEventHandler;
  dataLoadErrorMessage?: string;
  dataLoadNotFoundMessage?: string;
  submitErrorMessage?: string;
  secondaryContainerClass?: string;
  status: FormStatus;
  dataStatuses?: DataStatus[];
  onSubmit: React.FormEventHandler;
  children: React.ReactNode;
};

const DataForm = ({
  title,
  successMessage,
  successActionMessage,
  successAction,
  dataLoadErrorMessage,
  dataLoadNotFoundMessage,
  submitErrorMessage,
  secondaryContainerClass,
  status,
  dataStatuses,
  onSubmit,
  children,
}: FormProps) => {
  function anyStatusEquals(
    statuses: DataStatus[] | undefined,
    equals: DataStatus
  ): boolean {
    if (statuses !== undefined && statuses.length > 0) {
      for (let i = 0; i < statuses.length; i++) {
        if (statuses[i] === equals) return true;
      }
    }

    return false;
  }

  function allStatusesEqual(
    statuses: DataStatus[] | undefined,
    equals: DataStatus
  ): boolean {
    if (statuses !== undefined && statuses.length > 0) {
      for (let i = 0; i < statuses.length; i++) {
        if (statuses[i] !== equals) return false;
      }
      return true;
    }

    return false;
  }

  if (successMessage !== undefined && status === FormStatus.Success) {
    return (
      <Card flexDirection='column'>
        <Container alignment={Alignment.Center}>
          <StatusText text={successMessage} error={false} />
          {successActionMessage !== undefined ? (
            <Container
              padding={[
                Spacing.Medium,
                Spacing.None,
                Spacing.None,
                Spacing.None,
              ]}
            >
              <StatusText
                text={successActionMessage!}
                error={false}
                onClick={successAction}
              />
            </Container>
          ) : (
            <></>
          )}
        </Container>
      </Card>
    );
  }

  if (
    dataLoadErrorMessage !== undefined &&
    (anyStatusEquals(dataStatuses, DataStatus.Error) ||
      anyStatusEquals(dataStatuses, DataStatus.Fail))
  ) {
    return (
      <Card flexDirection='column'>
        <Container alignment={Alignment.Center}>
          <StatusText text={dataLoadErrorMessage} error={true} />
        </Container>
      </Card>
    );
  }

  if (
    dataLoadNotFoundMessage !== undefined &&
    anyStatusEquals(dataStatuses, DataStatus.NotFound)
  ) {
    return (
      <Card flexDirection='column'>
        <Container alignment={Alignment.Center}>
          <StatusText text={dataLoadNotFoundMessage} error={true} />
        </Container>
      </Card>
    );
  }

  return (
    <form onSubmit={onSubmit} className={css.form}>
      <Visibility
        visible={
          status === FormStatus.Submitting ||
          anyStatusEquals(dataStatuses, DataStatus.Loading)
        }
      >
        <div className={css.overlay}>
          <Card flexDirection='column'>
            <Spinner />
          </Card>
        </div>
      </Visibility>
      <Visibility
        visible={
          dataStatuses === undefined ||
          allStatusesEqual(dataStatuses, DataStatus.Ready)
        }
      >
        <Card
          flexDirection='column'
          title={title}
          secondaryClass={secondaryContainerClass}
        >
          <Visibility
            visible={
              submitErrorMessage !== undefined && StatusUtil.isFail(status)
            }
          >
            <Container
              alignment={Alignment.Center}
              margin={[
                Spacing.None,
                Spacing.None,
                Spacing.Medium,
                Spacing.None,
              ]}
            >
              <StatusText text={submitErrorMessage!} error={true} />
            </Container>
          </Visibility>
          {children}
        </Card>
      </Visibility>
    </form>
  );
};

export default DataForm;
